import { Box, Card, CardHeader, Divider, Link, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { BackupJobStatus, DatabaseResponseDto, ValidationJobStatus } from "~/gen/api/backup";
import { useLogViewerContext } from "../backups/LogViewer";
import numeral from 'numeral';
import InformationCircleIcon from '~/icons/InformationCircle';
import { useUserTimeFormat } from "~/utils/userTimeFormat";


export interface DatabaseSummaryProps {
  databaseInfo: DatabaseResponseDto
}

const getBackupLog = (dbInfo: DatabaseResponseDto) => {
  return [
    '####################',
    '# Backup Error Log #',
    '####################',
    '',
    dbInfo.backupFailedLog || '',
    ''
  ].join('\n')
}

const getValidationLog = (dbInfo: DatabaseResponseDto) => {
  return [
    '########################',
    '# Validation Error Log #',
    '########################',
    '',
    dbInfo.validationFailedLog || '',
    ''
  ].join('\n')
}

const DatabaseSummary: FC<DatabaseSummaryProps> = ({
  databaseInfo,
}) => {
  const bag = useMemo(() => {
    const lastBackupTime = databaseInfo.lastBackupTime ? new Date(databaseInfo.lastBackupTime) : null
    const lastValidationTime = databaseInfo.lastValidationTime ? new Date(databaseInfo.lastValidationTime) : null
  
    const hasError = {
      backup: databaseInfo.backupJobStatus === BackupJobStatus.Failed,
      validate: databaseInfo.validationJobStatus === ValidationJobStatus.Failed
    }
    const errorLog =
      hasError.backup && hasError.validate // both has error, combine error logs, sort by time
      ? (
        lastBackupTime && lastValidationTime 
        ? lastBackupTime > lastValidationTime
        ? [getBackupLog(databaseInfo), getValidationLog(databaseInfo)].join('\n')
        : [getValidationLog(databaseInfo), getBackupLog(databaseInfo)].join('\n')
        : '' /* never should be here */)
  
      : hasError.backup // show individual error log
      ? getBackupLog(databaseInfo)
      : hasError.validate
      ? getValidationLog(databaseInfo)
      : ''
  
    const statusText =
      hasError.backup && hasError.validate // both has error, show the latest error status text
      ? (
        lastBackupTime && lastValidationTime
        ? lastBackupTime > lastValidationTime
        ? (databaseInfo.backupJobStatusText)
        : (databaseInfo.validationJobStatusText)
        : null /* never should be here */)
  
      : hasError.backup // for individual error, show corresponding status text
      ? (databaseInfo.backupJobStatusText)
      : hasError.validate
      ? (databaseInfo.validationJobStatusText)
  
      : (// both has no error, show the lastest event related status text
        lastBackupTime && lastValidationTime
        ? lastBackupTime > lastValidationTime
        ? (databaseInfo.backupJobStatusText)
        : (databaseInfo.validationJobStatusText)
  
        // edge case: backup/validation not ever performed, which ever happenned shows its status text
        : lastValidationTime 
        ? (databaseInfo.validationJobStatusText)
        : lastBackupTime
        ? (databaseInfo.backupJobStatusText)
        : null)
    
    return {
      hasError: hasError.backup || hasError.validate,
      lastBackupTime,
      lastValidationTime,
      errorLog,
      statusText,
    }
  }, [databaseInfo])

  const { showLogInViewer } = useLogViewerContext()

  const format = useUserTimeFormat()
  return (
    <Card>
      <CardHeader title="Summary" />
      <Divider />
      <Table>
        <TableBody>
          <DatabaseInfoLine name="Engine">
            <Typography
              color="textSecondary"
              variant="body2"
            >
              {databaseInfo.engineVersion + ' ' + databaseInfo.engineEdition}
            </Typography>
          </DatabaseInfoLine>
          <DatabaseInfoLine name="Size">
            <Typography
              color="textSecondary"
              variant="body2"
            >
              {databaseInfo.sizeText &&
                databaseInfo.sizeText.toUpperCase()
                  .replace(/\d+/, (m) => numeral(m).format('0,0'))
              }
            </Typography>
          </DatabaseInfoLine>
          <DatabaseInfoLine name="Last Backup">
            {bag.lastBackupTime ?
              <Typography
                color="textSecondary"
                variant="body2"
              >
                Backup Ended:
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ pl: 1 }}
                >
                  {format(bag.lastBackupTime, 'Pp')}
                </Typography>
              </Typography> : null
            }
            {bag.lastValidationTime ?
              <Typography
                color="textSecondary"
                variant="body2"
              >
                Validation Ended:
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ pl: 1 }}
                >
                  {format(bag.lastValidationTime, 'Pp')}
                </Typography>
              </Typography> : null
            }
          </DatabaseInfoLine>
          <DatabaseInfoLine name="Status">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: bag.hasError ? 'error.main' : 'text.secondary',
                }}
              >
                {bag.statusText}
              </Typography>
              {bag.hasError ?
                <Link
                  component="button"
                  color="secondary"
                  onClick={(e:any) => showLogInViewer(e, bag.errorLog || '')}
                  underline="hover"
                  sx={{ cursor: 'pointer'}}                  
                >
                  <Typography
                    variant="caption"
                  >
                    (view log)
                  </Typography>
                </Link> : null
              }
            </Box>
          </DatabaseInfoLine>

          <DatabaseInfoLine name="RTO">
            <Stack direction="row" sx={{ justifyContent: 'space-between'}}>
              <Typography
                color="textPrimary"
                variant="body2"
              >
                {databaseInfo.rto ? formatMinutes(databaseInfo.rto) : 'N/A'}
              </Typography>
              <Tooltip title="RTO (Restore Time Objective) is OnData's estimation of how long it will take to restore and prepare your database for usage at OnData.">
                <InformationCircleIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </DatabaseInfoLine>
          <DatabaseInfoLine name="RPO">
          <Stack direction="row" sx={{ justifyContent: 'space-between'}}>
            <Typography
              color="textPrimary"
              variant="body2"
            >
              {databaseInfo.rpo ? formatMinutes(databaseInfo.rpo) : 'N/A'}
            </Typography>
            <Tooltip title="RPO (Restore Point Objective) is OnData's estimation of the amount of data that would be lost if an emergency restoration of the database is required, based on the backups that are available to OnData.">
              <InformationCircleIcon fontSize="small" />
            </Tooltip>
          </Stack>
          </DatabaseInfoLine>
        </TableBody>
      </Table>
    </Card>
  )
}

export default DatabaseSummary;

const DatabaseInfoLine: FC<{name: string}> = ({
  name,
  children,
}) => {
  return (
    <TableRow>
      <TableCell>
        <Typography
          color="textPrimary"
          variant="subtitle2"
        >
          {name}
        </Typography>
      </TableCell>
      <TableCell>
        {children}
      </TableCell>
    </TableRow>
  )
}

const timeUnits = [
  { unit: 'days', scale: 60 * 24 },
  { unit: 'hours', scale: 60 },
  { unit: 'minutes', scale: 1 },
]

const formatMinutes = (x: number) => {
  let m = Math.floor(x)
  return timeUnits.reduce((acc, u) => {
    if (acc) return acc
    if (m / u.scale > 1) {
      switch(u.unit) {
        case 'days':
          return `${(m / u.scale).toFixed(1)} ${u.unit}`
        case 'hours':
          return `${(m / u.scale).toFixed(0)} hours, ${(m % u.scale)} minutes`
        case 'minutes':
        default:
          return `${m} ${u.unit}`
      }
    } 
    else return ''
  }, '')
}
